// ============================================================================
// Grant Credit dialog — single-brand version, for use from a table row action
// ============================================================================
// The full-page /brand-credits flow (BrandCredits.tsx GrantPanel) starts from a
// brand SEARCH, because that page is a standalone destination. Here the brand is
// already known — the admin clicked a row — so this is the same money action
// with the picker removed, meant to be dropped onto any "grant credit to THIS
// brand" affordance (Brands table row, brand detail sheet, …).
//
// Nothing about the authorization is re-implemented here: every check (permission,
// per-grant/rolling-24h caps, self-dealing) is read from
// admin_my_credit_grant_context() — the same function admin_grant_brand_credit()
// enforces against — via fetchCreditGrantContext(). This component only decides
// whether to let the "Credit" button be clickable; the database decides whether
// the grant actually happens.
// ============================================================================

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Wallet, Loader2, ShieldAlert, Ban, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { usd } from '@/lib/refunds';
import { logCreate } from '@/lib/audit-log';
import {
  fetchCreditGrantContext, grantBrandCredit, parseDollarsToCents, TRANSACTION_PLATFORM_OPTIONS,
  OUT_OF_POCKET_PLATFORM,
} from '@/lib/adminCredits';

const QUICK_AMOUNTS = [100, 250, 500, 1000];

export interface GrantCreditTarget {
  id: string;
  name?: string | null;
}

export function GrantCreditDialog({ brand, onOpenChange, onGranted }: {
  /** null = closed. Passing a new brand while open resets the form. */
  brand: GrantCreditTarget | null;
  onOpenChange: (open: boolean) => void;
  onGranted?: () => void;
}) {
  const open = !!brand;
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [platform, setPlatform] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // One key per opened dialog: retries of THIS grant collapse into a no-op:
  // a double-click or a stale tab replays instead of granting twice.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!brand) return;
    setAmount('');
    setReason('');
    setPlatform('');
    setTransactionId('');
    setNote('');
    setError(null);
    setIdemKey(crypto.randomUUID());
  }, [brand?.id]);

  // Shares its cache key with any other "context for this brand" reader on the
  // page (e.g. a balance summary next to this button) — same brand, same
  // permission snapshot, one request.
  const { data: context, isLoading, isError, error: contextError } = useQuery({
    queryKey: ['credit-grant-context', brand?.id],
    queryFn: () => fetchCreditGrantContext(brand?.id ?? null),
    enabled: open,
  });

  const parsed = parseDollarsToCents(amount);
  const amountCents = parsed.cents;
  const limits = context?.limits;
  const liveBrand = context?.brand;
  const balanceCents = liveBrand?.balance_cents ?? 0;
  const selfDealing = liveBrand?.is_member_of_brand === true;
  // BMC funding itself has no external payment to point at — the DB
  // (admin_grant_brand_credit) drops the transaction ID requirement for this
  // one platform only. Every other platform keeps it mandatory.
  const isOutOfPocket = platform === OUT_OF_POCKET_PLATFORM;

  const blocker = !context
    ? null
    : !context.can_grant
      ? (context.blocked_reason ?? 'You are not permitted to grant brand credit.')
      : parsed.error
        ? parsed.error
        : amountCents <= 0
          ? 'Enter an amount'
          : limits && amountCents < limits.min_grant_cents
            ? `Minimum grant is ${usd(limits.min_grant_cents)}`
            : limits && amountCents > limits.remaining_24h_cents
              ? `Over your remaining 24h allowance of ${usd(limits.remaining_24h_cents)}`
              : reason.trim().length < 3
                  ? 'Add a reason — the brand sees it on their wallet history'
                  : reason.trim().length > 500
                    ? 'Reason must be 500 characters or fewer'
                    // Mandatory — credit can only be granted for money that
                    // moved somewhere real. The database refuses without both.
                    : !platform
                    ? 'Pick the payment method this credit corresponds to'
                    : (!isOutOfPocket && !transactionId.trim())
                      ? 'Enter the transaction ID for that payment'
                      : null;

  async function submit() {
    if (!brand || blocker) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await grantBrandCredit({
        brandId: brand.id,
        amountCents,
        reason: reason.trim(),
        transactionPlatform: platform,
        transactionId: transactionId.trim(),
        internalNote: note.trim() || undefined,
        idempotencyKey: idemKey,
      });

      logCreate('brand_credit_grants', result.grant_id, {
        brand_id: result.brand_id,
        amount_cents: result.granted_cents,
        reason: reason.trim(),
        transaction_platform: result.transaction_platform,
        transaction_id: result.transaction_id,
        internal_note: note.trim() || null,
        ledger_entry_id: result.ledger_id,
        balance_after_cents: result.balance_cents,
      }, {
        status: 'success',
        brand_name: result.brand_name,
        replayed: result.replayed,
        description: `Granted ${usd(result.granted_cents)} wallet credit to ${result.brand_name ?? result.brand_id}${result.replayed ? ' (replay — no new credit)' : ''}`,
      });

      toast.success(
        result.replayed
          ? 'That grant had already been applied — no new credit was created.'
          : `${usd(result.granted_cents)} credited to ${result.brand_name ?? 'the brand'}`,
        { description: `New balance ${usd(result.balance_cents)}` },
      );

      queryClient.invalidateQueries({ queryKey: ['brand-users'] });
      queryClient.invalidateQueries({ queryKey: ['credit-grant-context'] });
      queryClient.invalidateQueries({ queryKey: ['credit-grant-log'] });
      queryClient.invalidateQueries({ queryKey: ['credit-grant-stats'] });
      onGranted?.();
      onOpenChange(false);
    } catch (e: any) {
      const message = e?.message ?? 'Credit grant failed';
      // Refusals the database itself raised were already written server-side
      // (that row IS the transaction that couldn't complete), but this trail
      // has to show the attempt too — otherwise the admin audit log implies
      // nothing happened.
      logCreate('brand_credit_grants', brand.id, {
        brand_id: brand.id, amount_cents: amountCents, reason: reason.trim(),
      }, {
        status: 'failed',
        error: message,
        brand_name: brand.name,
        description: `Credit grant of ${usd(amountCents)} to ${brand.name ?? brand.id} was rejected`,
      });
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-violet-600" />
            Grant credit — {brand?.name ?? 'this brand'}
          </DialogTitle>
          <DialogDescription>
            Adds spendable wallet credit. It's applied to the brand's next hire before their card —
            treat it as cash out the door.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <Skeleton className="h-48 w-full rounded-lg" />}

        {isError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{(contextError as any)?.message ?? 'Could not confirm your credit permissions.'}</span>
          </div>
        )}

        {context && !context.can_grant && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{context.blocked_reason ?? 'Your account cannot grant brand credit.'}</span>
          </div>
        )}

        {context?.can_grant && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-muted-foreground">Current balance</span>
                <span className="font-semibold tabular-nums">{usd(balanceCents)}</span>
              </div>
              {liveBrand && liveBrand.held_cents > 0 && (
                <div className="mt-0.5 flex items-baseline justify-between text-xs text-muted-foreground">
                  <span>Reserved</span><span className="tabular-nums">{usd(liveBrand.held_cents)}</span>
                </div>
              )}
              {amountCents > 0 && (
                <div className="mt-1.5 flex items-baseline justify-between border-t pt-1.5">
                  <span className="text-xs text-muted-foreground">After this grant</span>
                  <span className="text-sm font-semibold tabular-nums text-violet-700">
                    {usd(balanceCents + amountCents)}
                  </span>
                </div>
              )}
            </div>

            {selfDealing && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <Ban className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  You're a member of this brand's team, so you can't credit it — the database refuses this
                  combination regardless of your permission. Another admin has to issue it.
                </span>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="gcd-amount" className="text-sm font-medium">Amount</label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-36">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    id="gcd-amount"
                    value={amount}
                    onChange={(e) => { setAmount(e.target.value); setError(null); }}
                    placeholder="250.00"
                    inputMode="decimal"
                    className="pl-6 font-semibold tabular-nums"
                    autoFocus
                  />
                </div>
                {QUICK_AMOUNTS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setAmount(String(d)); setError(null); }}
                    className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      amountCents === d * 100 ? 'border-violet-400 bg-violet-50 text-violet-800' : 'hover:bg-muted/60'
                    }`}
                  >
                    ${d}
                  </button>
                ))}
              </div>
              {parsed.error && <p className="text-xs text-red-500">{parsed.error}</p>}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="gcd-reason" className="text-sm font-medium">
                Reason <span className="font-normal text-muted-foreground">— shown to the brand</span>
              </label>
              <Input
                id="gcd-reason"
                value={reason}
                onChange={(e) => { setReason(e.target.value.slice(0, 500)); setError(null); }}
                placeholder="Goodwill credit for the delayed campaign launch"
                maxLength={500}
              />
              <p className={`text-[11px] ${reason.trim().length > 500 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {reason.trim().length > 500 ? 'Max 500 characters allowed' : `${reason.length}/500 characters`}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="gcd-platform" className="text-sm font-medium">
                  Payment method <span className="font-normal text-muted-foreground">— required</span>
                </label>
                <Select value={platform} onValueChange={(v) => { setPlatform(v); setError(null); }}>
                  <SelectTrigger id="gcd-platform"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_PLATFORM_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="gcd-txn" className="text-sm font-medium">
                  Transaction ID{' '}
                  <span className="font-normal text-muted-foreground">
                    {isOutOfPocket ? '— optional' : '— required'}
                  </span>
                </label>
                <Input
                  id="gcd-txn"
                  value={transactionId}
                  onChange={(e) => { setTransactionId(e.target.value); setError(null); }}
                  placeholder={isOutOfPocket ? 'Optional — e.g. an internal ticket reference' : 'e.g. Whop charge ID, wire reference'}
                  maxLength={200}
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              {isOutOfPocket
                ? "BMC Funding Out of Pocket is the one platform with no external payment to reference — the transaction ID isn't required for it."
                : 'Credit can only be granted for money that moved somewhere real — this is what the DB requires it be tied to.'}
            </p>

            <div className="space-y-1.5">
              <label htmlFor="gcd-note" className="text-sm font-medium">
                Internal note <span className="font-normal text-muted-foreground">— never shown to the brand (optional)</span>
              </label>
              <Textarea
                id="gcd-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Approved by… / ticket reference"
              />
            </div>

            {limits && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" /> Your rolling 24h allowance</span>
                  <span className="font-medium text-foreground">
                    {usd(limits.remaining_24h_cents)} left of {usd(limits.daily_limit_cents)}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-violet-500 transition-all"
                    style={{
                      width: `${limits.daily_limit_cents > 0
                        ? Math.min(100, Math.round((limits.granted_24h_cents / limits.daily_limit_cents) * 100))
                        : 0}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <p className="font-semibold">Grant refused</p>
                <p className="mt-0.5">{error}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700"
            disabled={!context?.can_grant || !!blocker || submitting}
            onClick={submit}
          >
            {submitting
              ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Crediting…</>
              : amountCents > 0 ? `Credit ${usd(amountCents)}` : 'Credit brand'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
